import { useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink, Info, ShoppingCart } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { loadBringScript } from '@/lib/bring-widget'
import { Recipe } from '@/types/recipe'

interface GetBringWidgetProps {
  recipe: Recipe
  servings?: number
  className?: string
}

export function GetBringWidget({ recipe, servings, className = '' }: GetBringWidgetProps) {
  const widgetRef = useRef<HTMLDivElement>(null)
  const [isScriptReady, setIsScriptReady] = useState<boolean>(Boolean(window.bringwidgets?.import?.render))
  const requestedServings = servings || recipe.servings || 4
  const baseServings = recipe.servings || 4

  const bringImportUrl = useMemo(() => {
    if (typeof window === 'undefined' || !recipe.id) {
      return ''
    }

    const endpoint = new URL('/api/bring/recipe', window.location.origin)
    endpoint.searchParams.set('id', recipe.id)

    return endpoint.toString()
  }, [recipe.id])

  useEffect(() => {
    void loadBringScript()
      .then(() => setIsScriptReady(true))
      .catch(() => setIsScriptReady(false))
  }, [])

  useEffect(() => {
    if (!bringImportUrl || !widgetRef.current || !recipe.is_public) {
      return
    }

    const renderWidget = () => {
      if (!widgetRef.current || !window.bringwidgets?.import?.render) {
        return
      }

      widgetRef.current.innerHTML = ''
      window.bringwidgets.import.render(widgetRef.current, {
        url: bringImportUrl,
        language: 'en',
        theme: 'light',
        baseQuantity: String(baseServings),
        requestedQuantity: String(requestedServings),
      })
    }

    void loadBringScript()
      .then(() => {
        setIsScriptReady(true)
        renderWidget()
      })
      .catch(() => {
        setIsScriptReady(false)
      })
  }, [baseServings, bringImportUrl, recipe.is_public, requestedServings])

  if (!recipe.ingredients || recipe.ingredients.length === 0) {
    return null
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Bring import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {recipe.is_public ? (
          <>
            <p className="text-sm text-gray-600">
              Importeer de ingrediënten van dit recept direct in Bring met de officiële importknop.
            </p>

            <p className="text-xs text-gray-500">
              Bring gebruikt nu {requestedServings} {requestedServings === 1 ? 'portie' : 'porties'} voor deze import.
            </p>

            {!isScriptReady ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                Bring-knop laden...
              </div>
            ) : null}

            <div ref={widgetRef} className="min-h-10" />

            <Button asChild variant="outline" className="w-full">
              <a href={bringImportUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open publieke Bring-bron
              </a>
            </Button>
          </>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                Bring kan alleen importeren vanuit een publiek bereikbare bron. Maak dit recept eerst publiek als je het direct naar Bring wilt sturen.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
